import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { topicMeta, ICON_OPTIONS } from "@/lib/topicMeta";
import {
  LifeBuoy, LogOut, Plus, Pencil, Trash2, X, Loader2, FileText,
  FolderOpen, ThumbsUp, Eye, Inbox, Mail, XCircle, Check, Clock, SlidersHorizontal,
} from "lucide-react";

/* ---------------- Confirm dialog ---------------- */
function ConfirmDialog({ open, title, message, onConfirm, onCancel }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 backdrop-blur-sm p-4" onClick={onCancel}>
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-semibold text-slate-900">{title}</h3>
        <p className="text-sm text-slate-500 mt-2">{message}</p>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onCancel} className="h-10 px-4 rounded-lg border border-slate-200 font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
          <button data-testid="confirm-delete-btn" onClick={onConfirm} className="h-10 px-4 rounded-lg bg-[#D93025] hover:bg-red-700 text-white font-semibold">Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Modal shell ---------------- */
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/40 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-heading text-lg font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="grid place-items-center w-9 h-9 rounded-full text-slate-400 hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

const inputCls = "w-full h-11 px-4 rounded-lg border border-slate-200 outline-none focus:border-[#1A73E8] focus:ring-4 focus:ring-blue-100 transition-all";
const labelCls = "block text-sm font-medium text-slate-700 mb-1.5";

/* ---------------- Topic form ---------------- */
function TopicForm({ initial, onSaved, onClose }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [iconKey, setIconKey] = useState(initial?.icon_key || "account");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Topic name is required");
    setSaving(true);
    try {
      const payload = { name, description, icon_key: iconKey };
      if (initial) await api.put(`/admin/topics/${initial.id}`, payload);
      else await api.post("/admin/topics", payload);
      toast.success(initial ? "Topic updated" : "Topic created");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4">
      <div>
        <label className={labelCls}>Topic name</label>
        <input data-testid="admin-topic-input-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Gsupport Mail" />
      </div>
      <div>
        <label className={labelCls}>Description</label>
        <textarea className={inputCls + " h-auto py-3"} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short summary shown on the topic card" />
      </div>
      <div>
        <label className={labelCls}>Icon</label>
        <div className="flex flex-wrap gap-2">
          {ICON_OPTIONS.map((opt) => {
            const { Icon, color, bg } = topicMeta(opt.key);
            const active = iconKey === opt.key;
            return (
              <button type="button" key={opt.key} onClick={() => setIconKey(opt.key)}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${active ? "border-[#1A73E8] ring-2 ring-blue-100" : "border-slate-200 hover:border-slate-300"}`}>
                <span className="grid place-items-center w-6 h-6 rounded" style={{ backgroundColor: bg, color }}><Icon className="w-3.5 h-3.5" /></span>
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="h-11 px-5 rounded-lg border border-slate-200 font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
        <button data-testid="admin-save-topic-btn" type="submit" disabled={saving} className="h-11 px-5 rounded-lg bg-[#1A73E8] hover:bg-[#174EA6] text-white font-semibold inline-flex items-center gap-2 disabled:opacity-60">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save topic
        </button>
      </div>
    </form>
  );
}

/* ---------------- Article form ---------------- */
function ArticleForm({ initial, topics, onSaved, onClose }) {
  const [title, setTitle] = useState(initial?.title || "");
  const [topicSlug, setTopicSlug] = useState(initial?.topic_slug || topics[0]?.slug || "");
  const [section, setSection] = useState(initial?.section || "Get started");
  const [readTime, setReadTime] = useState(initial?.read_time || 3);
  const [excerpt, setExcerpt] = useState(initial?.excerpt || "");
  const [body, setBody] = useState(initial?.body || "");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error("Title is required");
    if (!topicSlug) return toast.error("Please pick a topic");
    setSaving(true);
    try {
      const payload = { title, topic_slug: topicSlug, section, excerpt, body, read_time: Number(readTime) || 1 };
      if (initial) await api.put(`/admin/articles/${initial.id}`, payload);
      else await api.post("/admin/articles", payload);
      toast.success(initial ? "Article updated" : "Article created");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4">
      <div>
        <label className={labelCls}>Title</label>
        <input data-testid="admin-article-input-title" className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Article title" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-1">
          <label className={labelCls}>Topic</label>
          <select data-testid="admin-article-topic-select" className={inputCls} value={topicSlug} onChange={(e) => setTopicSlug(e.target.value)}>
            {topics.map((t) => <option key={t.id} value={t.slug}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Section</label>
          <input className={inputCls} value={section} onChange={(e) => setSection(e.target.value)} placeholder="Get started" />
        </div>
        <div>
          <label className={labelCls}>Read time (min)</label>
          <input type="number" min="1" className={inputCls} value={readTime} onChange={(e) => setReadTime(e.target.value)} />
        </div>
      </div>
      <div>
        <label className={labelCls}>Excerpt</label>
        <textarea className={inputCls + " h-auto py-3"} rows={2} value={excerpt} onChange={(e) => setExcerpt(e.target.value)} placeholder="One-line summary shown in lists and search" />
      </div>
      <div>
        <label className={labelCls}>Body</label>
        <textarea data-testid="admin-article-input-body" className={inputCls + " h-auto py-3 font-mono text-sm"} rows={10} value={body} onChange={(e) => setBody(e.target.value)} placeholder={"Full article text.\n\nSeparate paragraphs with a blank line.\nStart a line with '- ' for bullet points."} />
        <p className="text-xs text-slate-400 mt-1.5">Tip: blank line = new paragraph. A short line becomes a heading. Lines starting with “- ” become bullets.</p>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="h-11 px-5 rounded-lg border border-slate-200 font-medium text-slate-700 hover:bg-slate-50">Cancel</button>
        <button data-testid="admin-save-article-btn" type="submit" disabled={saving} className="h-11 px-5 rounded-lg bg-[#1A73E8] hover:bg-[#174EA6] text-white font-semibold inline-flex items-center gap-2 disabled:opacity-60">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save article
        </button>
      </div>
    </form>
  );
}

/* ---------------- Support page settings form ---------------- */
function SupportSettingsForm({ onSaved }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/support-settings").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data)
    return <div className="py-16 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    if (!data.heading.trim()) return toast.error("Heading is required");
    setSaving(true);
    try {
      await api.put("/admin/support-settings", data);
      toast.success("Support page updated");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-5 max-w-2xl">
      <div>
        <label className={labelCls}>Page heading</label>
        <input data-testid="support-settings-heading" className={inputCls} value={data.heading} onChange={set("heading")} />
      </div>
      <div>
        <label className={labelCls}>Intro text</label>
        <textarea data-testid="support-settings-intro" className={inputCls + " h-auto py-3"} rows={3} value={data.intro} onChange={set("intro")} />
      </div>

      <div className="border-t border-slate-100 pt-5">
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <input data-testid="support-settings-show-tool" type="checkbox" className="w-4 h-4 accent-[#1A73E8]" checked={!!data.show_remote_tool} onChange={(e) => setData({ ...data, show_remote_tool: e.target.checked })} />
          <span className="text-sm font-medium text-slate-700">Show the remote support tool download section</span>
        </label>
      </div>

      {data.show_remote_tool && (
        <>
          <div>
            <label className={labelCls}>Remote tool — title</label>
            <input className={inputCls} value={data.remote_title} onChange={set("remote_title")} />
          </div>
          <div>
            <label className={labelCls}>Remote tool — description</label>
            <textarea className={inputCls + " h-auto py-3"} rows={2} value={data.remote_description} onChange={set("remote_description")} />
          </div>
          <div>
            <label className={labelCls}>Download button label</label>
            <input className={inputCls} value={data.remote_button_label} onChange={set("remote_button_label")} />
          </div>
        </>
      )}

      <div className="flex justify-end pt-2">
        <button data-testid="support-settings-save-btn" type="submit" disabled={saving} className="h-11 px-6 rounded-lg bg-[#1A73E8] hover:bg-[#174EA6] text-white font-semibold inline-flex items-center gap-2 disabled:opacity-60">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save changes
        </button>
      </div>
    </form>
  );
}

/* ---------------- Dashboard ---------------- */
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("articles");
  const [topics, setTopics] = useState([]);
  const [articles, setArticles] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const [topicModal, setTopicModal] = useState(null); // {editing?}
  const [articleModal, setArticleModal] = useState(null);
  const [confirm, setConfirm] = useState(null); // {type,id,name}

  const load = useCallback(async () => {
    const [t, a, r] = await Promise.all([
      api.get("/topics"),
      api.get("/articles"),
      api.get("/admin/requests"),
    ]);
    setTopics(t.data);
    setArticles(a.data);
    setRequests(r.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const doDelete = async () => {
    const c = confirm;
    setConfirm(null);
    try {
      if (c.type === "topic") await api.delete(`/admin/topics/${c.id}`);
      else if (c.type === "request") await api.delete(`/admin/requests/${c.id}`);
      else await api.delete(`/admin/articles/${c.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const setRequestStatus = async (id, status) => {
    try {
      await api.patch(`/admin/requests/${id}`, { status });
      toast.success(status === "resolved" ? "Marked resolved" : "Reopened");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update");
    }
  };

  if (user === null || (user && loading))
    return <div className="py-32 grid place-items-center text-slate-400"><Loader2 className="w-7 h-7 animate-spin" /></div>;
  if (!user) return null;

  const totalHelpful = articles.reduce((s, a) => s + (a.helpful_yes || 0), 0);
  const openRequests = requests.filter((r) => r.status !== "resolved").length;

  return (
    <main data-testid="admin-dashboard" className="min-h-screen bg-[#F8F9FA]">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#1A73E8] text-white"><LifeBuoy className="w-5 h-5" /></span>
            <span className="font-heading font-bold text-lg text-slate-900">Admin Console</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-medium text-slate-600 hover:bg-slate-100">
              <Eye className="w-4 h-4" /> <span className="hidden sm:inline">View site</span>
            </button>
            <button data-testid="admin-logout-btn" onClick={() => { logout(); navigate("/"); }} className="inline-flex items-center gap-2 h-10 px-4 rounded-full text-sm font-semibold text-slate-700 border border-slate-200 hover:bg-slate-50">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Topics", value: topics.length, Icon: FolderOpen, color: "#1A73E8", bg: "#E8F0FE" },
            { label: "Articles", value: articles.length, Icon: FileText, color: "#188038", bg: "#E6F4EA" },
            { label: "Helpful votes", value: totalHelpful.toLocaleString(), Icon: ThumbsUp, color: "#B06000", bg: "#FEF7E0" },
            { label: "Open requests", value: openRequests, Icon: Inbox, color: "#D93025", bg: "#FCE8E6" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-2xl border border-slate-200 p-6 flex items-center gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-xl" style={{ backgroundColor: s.bg, color: s.color }}><s.Icon className="w-6 h-6" /></span>
              <div>
                <div className="font-heading text-2xl font-bold text-slate-900">{s.value}</div>
                <div className="text-sm text-slate-500">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center justify-between mb-5">
          <div className="inline-flex p-1 rounded-full bg-slate-100">
            {["articles", "topics", "requests", "support"].map((t) => (
              <button key={t} data-testid={`admin-tab-${t}`} onClick={() => setTab(t)}
                className={`h-9 px-5 rounded-full text-sm font-semibold capitalize transition-colors ${tab === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
                {t === "requests" && openRequests > 0 ? `Requests (${openRequests})` : t === "support" ? "Support page" : t}
              </button>
            ))}
          </div>
          {tab === "articles" ? (
            <button data-testid="admin-create-article-btn" onClick={() => setArticleModal({})} className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#1A73E8] hover:bg-[#174EA6] text-white text-sm font-semibold">
              <Plus className="w-4 h-4" /> New article
            </button>
          ) : tab === "topics" ? (
            <button data-testid="admin-create-topic-btn" onClick={() => setTopicModal({})} className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#1A73E8] hover:bg-[#174EA6] text-white text-sm font-semibold">
              <Plus className="w-4 h-4" /> New topic
            </button>
          ) : null}
        </div>

        {/* Articles list */}
        {tab === "articles" && (
          <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
            {articles.length === 0 && <p className="p-8 text-center text-slate-400 text-sm">No articles yet. Create your first one.</p>}
            {articles.map((a) => {
              const { Icon, color, bg } = topicMeta(a.topic_icon);
              return (
                <div key={a.id} data-testid="admin-article-row" className="flex items-center gap-4 px-5 py-4">
                  <span className="grid place-items-center w-9 h-9 rounded-lg shrink-0" style={{ backgroundColor: bg, color }}><Icon className="w-4.5 h-4.5" /></span>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-slate-900 truncate">{a.title}</h4>
                    <div className="text-xs text-slate-400 mt-0.5">{a.topic_name} · {a.section} · {a.read_time} min · {a.helpful_yes} helpful</div>
                  </div>
                  <button data-testid="admin-edit-article-btn" onClick={() => setArticleModal({ editing: a })} className="grid place-items-center w-9 h-9 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-[#1A73E8]"><Pencil className="w-4 h-4" /></button>
                  <button data-testid="admin-delete-article-btn" onClick={() => setConfirm({ type: "article", id: a.id, name: a.title })} className="grid place-items-center w-9 h-9 rounded-lg text-slate-500 hover:bg-red-50 hover:text-[#D93025]"><Trash2 className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>
        )}

        {/* Topics list */}
        {tab === "topics" && (
          <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
            {topics.length === 0 && <p className="p-8 text-center text-slate-400 text-sm">No topics yet.</p>}
            {topics.map((t) => {
              const { Icon, color, bg } = topicMeta(t.icon_key);
              return (
                <div key={t.id} data-testid="admin-topic-row" className="flex items-center gap-4 px-5 py-4">
                  <span className="grid place-items-center w-10 h-10 rounded-lg shrink-0" style={{ backgroundColor: bg, color }}><Icon className="w-5 h-5" /></span>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-medium text-slate-900">{t.name}</h4>
                    <div className="text-xs text-slate-400 mt-0.5 truncate">{t.article_count} articles · {t.description}</div>
                  </div>
                  <button data-testid="admin-edit-topic-btn" onClick={() => setTopicModal({ editing: t })} className="grid place-items-center w-9 h-9 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-[#1A73E8]"><Pencil className="w-4 h-4" /></button>
                  <button data-testid="admin-delete-topic-btn" onClick={() => setConfirm({ type: "topic", id: t.id, name: t.name })} className="grid place-items-center w-9 h-9 rounded-lg text-slate-500 hover:bg-red-50 hover:text-[#D93025]"><Trash2 className="w-4 h-4" /></button>
                </div>
              );
            })}
          </div>
        )}

        {/* Requests inbox */}
        {tab === "requests" && (
          <div className="space-y-3">
            {requests.length === 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                <Inbox className="w-10 h-10 mx-auto text-slate-300" />
                <p className="text-slate-500 mt-3 text-sm">No support or cancellation requests yet.</p>
              </div>
            )}
            {requests.map((r) => {
              const isCancel = r.type === "cancellation";
              const resolved = r.status === "resolved";
              return (
                <div key={r.id} data-testid="admin-request-row" className={`bg-white rounded-2xl border p-5 ${resolved ? "border-slate-200 opacity-70" : "border-slate-200"}`}>
                  <div className="flex items-start gap-4">
                    <span className="grid place-items-center w-10 h-10 rounded-lg shrink-0" style={{ backgroundColor: isCancel ? "#FCE8E6" : "#E8F0FE", color: isCancel ? "#D93025" : "#1A73E8" }}>
                      {isCancel ? <XCircle className="w-5 h-5" /> : <Mail className="w-5 h-5" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isCancel ? "bg-red-50 text-[#D93025]" : "bg-blue-50 text-[#1A73E8]"}`}>
                          {isCancel ? "Cancellation" : "Support"}
                        </span>
                        {resolved ? (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-50 text-[#188038]">Resolved</span>
                        ) : (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-[#B06000]">New</span>
                        )}
                        <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(r.created_at).toLocaleDateString()}</span>
                      </div>
                      <h4 className="font-medium text-slate-900 mt-2">
                        {isCancel ? `Cancel request — ${r.reason}` : r.subject}
                      </h4>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {r.name} · <a href={`mailto:${r.email}`} className="text-[#1A73E8] hover:underline">{r.email}</a>
                        {isCancel && r.plan ? ` · ${r.plan} plan` : ""}
                        {!isCancel && r.topic ? ` · ${r.topic}` : ""}
                      </p>
                      {r.message && <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap bg-slate-50 rounded-lg p-3">{r.message}</p>}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <button
                        data-testid="admin-request-toggle-btn"
                        onClick={() => setRequestStatus(r.id, resolved ? "new" : "resolved")}
                        className={`grid place-items-center w-9 h-9 rounded-lg ${resolved ? "text-slate-500 hover:bg-slate-100" : "text-[#188038] hover:bg-green-50"}`}
                        title={resolved ? "Reopen" : "Mark resolved"}
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        data-testid="admin-request-delete-btn"
                        onClick={() => setConfirm({ type: "request", id: r.id, name: isCancel ? "this cancellation request" : r.subject })}
                        className="grid place-items-center w-9 h-9 rounded-lg text-slate-500 hover:bg-red-50 hover:text-[#D93025]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Support page settings */}
        {tab === "support" && (
          <div>
            <div className="flex items-center gap-2 mb-4 text-slate-500 text-sm">
              <SlidersHorizontal className="w-4 h-4" />
              <span>Edit the text visitors see on the <span className="font-medium text-slate-700">Get support</span> page. Changes go live instantly.</span>
            </div>
            <SupportSettingsForm />
          </div>
        )}
      </div>

      {topicModal && (
        <Modal title={topicModal.editing ? "Edit topic" : "New topic"} onClose={() => setTopicModal(null)}>
          <TopicForm initial={topicModal.editing} topics={topics} onSaved={load} onClose={() => setTopicModal(null)} />
        </Modal>
      )}
      {articleModal && (
        <Modal title={articleModal.editing ? "Edit article" : "New article"} onClose={() => setArticleModal(null)}>
          <ArticleForm initial={articleModal.editing} topics={topics} onSaved={load} onClose={() => setArticleModal(null)} />
        </Modal>
      )}
      <ConfirmDialog
        open={!!confirm}
        title={`Delete ${confirm?.type}?`}
        message={confirm?.type === "topic" ? `“${confirm?.name}” and all its articles will be permanently removed.` : `“${confirm?.name}” will be permanently removed.`}
        onConfirm={doDelete}
        onCancel={() => setConfirm(null)}
      />
    </main>
  );
}
